/* eslint-disable camelcase */
import React, { useMemo, useState } from 'react';
import {
  ResourceSet,
  useForceFetchExternalData,
  useResourceSets,
} from '../hooks/resourceSetHooks';
import {
  useCheckInResource,
  useResourcesInIncident,
} from '../hooks/resourceInIncidentHooks';
import { ResourceSite, useResourceSites } from '../hooks/resourceSiteHooks';
import { useActualIncidentId } from '../hooks/commonHooks';
import ResourceSetCard from '../cards/ResourceSetCard';
import { StylishNewButton } from '../../DesignSystems/New/StylishNewButton';
import LegacyPratusTable, {
  LegacyExpandRowConfig,
} from '../../DesignSystems/PratusTable/LegacyPratusTable';
import ResourceSetEditDrawer from '../ResourceSetEditDrawer';
import ResourceSetAddDrawer from '../ResourceSetAddDrawer';
import { Badge, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { selectGroupGuid } from '../../../slices/commonSelectors';
import StylishNewSelect from '../../DesignSystems/New/StylishNewSelect';
import useIsOrgAdmin from '../../../hooks/useIsOrgAdmin';

interface OrganizationResourcesViewProps {
  onNewResource?: () => void;
}

const OrganizationResourcesView: React.FC<OrganizationResourcesViewProps> = ({
  onNewResource,
}) => {
  const [modal, setModal] = useState<'new' | 'edit' | undefined>();
  const [selectedResource, setSelectedResource] = useState<
    ResourceSet | undefined
  >();
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedKind, setSelectedKind] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');
  const incidentId = useActualIncidentId();
  const groupGuid = useSelector(selectGroupGuid);
  const isOrgAdmin = useIsOrgAdmin();

  const resourceSets = useResourceSets();
  const { data: resourceSites = [] } = useResourceSites();
  const resourcesInIncident = useResourcesInIncident();
  const forceFetchExternalData = useForceFetchExternalData();
  const checkInResource = useCheckInResource();

  // Create a set of resource_set_ids that are in the incident
  const resourcesInIncidentSet = useMemo(() => {
    const set = new Set<string>();
    if (resourcesInIncident.data) {
      resourcesInIncident.data.forEach((resource) => {
        if (resource.resource_set_id) {
          // Resource is in incident if it's not fully checked out
          // Only fully checked out resources (not in_process) can be re-added
          if (resource.current_status !== 'checked_out') {
            set.add(resource.resource_set_id);
          }
        }
      });
    }
    return set;
  }, [resourcesInIncident.data]);

  // Get unique kinds and filter/sort resources
  const { availableKinds, filteredAndSortedResources } = useMemo(() => {
    const kinds = new Set<string>();
    let filtered = resourceSets.data || [];

    // Collect kinds
    filtered.forEach((resource) => {
      if (resource.kind) kinds.add(resource.kind);
    });

    // Filter by kind
    if (selectedKind && selectedKind !== 'all') {
      filtered = filtered.filter((r) => r.kind === selectedKind);
    }

    // Filter by search
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim();
      filtered = filtered.filter((resource) => {
        const searchableText = [
          resource.name || '',
          resource.kind || '',
          resource.type || '',
          resource.status || '',
          resource.description || '',
        ]
          .join(' ')
          .toLowerCase();
        return searchableText.includes(searchLower);
      });
    }

    // Sort by type, then kind, then name
    const sorted = [...filtered].sort((a, b) => {
      const typeCompare = (a.type || '').localeCompare(b.type || '');
      if (typeCompare !== 0) return typeCompare;
      const kindCompare = (a.kind || '').localeCompare(b.kind || '');
      if (kindCompare !== 0) return kindCompare;
      return (a.name || '').localeCompare(b.name || '');
    });

    return {
      availableKinds: Array.from(kinds).sort(),
      filteredAndSortedResources: sorted,
    };
  }, [resourceSets.data, selectedKind, searchText]);

  const handleAddToIncident = async (resourceSet: ResourceSet) => {
    try {
      await checkInResource.mutateAsync({
        resource_set_id: resourceSet.resource_set_id,
        incident_id: incidentId,
        group_guid: groupGuid,
        leader_name: '',
        personnel_count: resourceSet.quantity || 1,
        current_status: 'in_transit',
      });
      toast.success(`${resourceSet.name} added to incident`);
      // Force a re-render
      setRefreshKey((prev) => prev + 1);
      // Manually refetch with a slight delay
      setTimeout(async () => {
        await resourcesInIncident.refetch();
      }, 100);
    } catch (error) {
      toast.error(
        `Failed to add resource: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  const editRow = (row: ResourceSet) => {
    setSelectedResource(row);
    setModal('edit');
  };

  // Helper function to format location display
  const formatLocation = (location: any, siteId?: string) => {
    if (!location && !siteId) return '-';

    // If we have a site ID, try to find the site name
    if (siteId && resourceSites) {
      const site = resourceSites.find((s: ResourceSite) => s.id === siteId);
      if (site) return site.name;
    }

    // If we have location object, check for name first
    if (location) {
      // Check if location has a name field (from geolocation JSON)
      if (location.name) {
        return location.name;
      }
      // Fall back to coordinates if available
      if (location.lat || location.lng) {
        return `${location.lat?.toFixed(4)}, ${location.lng?.toFixed(4)}`;
      }
    }

    return '-';
  };

  const columns = [
    { dataField: 'name', text: 'Name', sort: true, attrs: { title: 'Name' } },
    { dataField: 'kind', text: 'Kind', sort: true, attrs: { title: 'Kind' } },
    { dataField: 'type', text: 'Type', sort: true, attrs: { title: 'Type' } },
    {
      dataField: 'quantity',
      text: 'Quantity',
      sort: true,
      attrs: { title: 'Quantity' },
    },
    {
      dataField: 'status',
      text: 'Status',
      sort: true,
      attrs: { title: 'Status' },
    },
    // Always show location columns
    {
      dataField: 'source_location',
      text: 'Source',
      isDummyField: true,
      formatter: (_cell: unknown, row: ResourceSet) =>
        formatLocation(row.source_location, row.source_resource_site_id),
      attrs: { title: 'Source Location' },
    },
    {
      dataField: 'geolocation',
      text: 'Current',
      formatter: (_cell: unknown, row: ResourceSet) =>
        formatLocation(row.geolocation, row.resource_site_id),
      attrs: { title: 'Current Location' },
    },
    {
      dataField: 'destination_location',
      text: 'Destination',
      isDummyField: true,
      formatter: (_cell: unknown, row: ResourceSet) =>
        formatLocation(
          row.destination_location,
          row.destination_resource_site_id
        ),
      attrs: { title: 'Destination Location' },
    },
    {
      dataField: 'is_external_resource',
      text: 'External',
      sort: true,
      attrs: { title: 'External Resource' },
    },
    {
      dataField: 'edit',
      text: 'Edit',
      isDummyField: true,
      formatter: (_cell: unknown, row: ResourceSet) => (
        <StylishNewButton
          className="button button--secondary button--sm"
          onClick={(e) => {
            e.stopPropagation();
            editRow(row);
          }}
        >
          Edit
        </StylishNewButton>
      ),
      attrs: { title: 'Edit Resource' },
    },
    {
      dataField: 'actions',
      text: 'Actions',
      isDummyField: true,
      formatter: (_cell: unknown, row: ResourceSet) => {
        const isInIncident = resourcesInIncidentSet.has(row.resource_set_id);

        if (isInIncident) {
          return <Badge bg="success">In Incident</Badge>;
        }

        return (
          <StylishNewButton
            className="button button--primary"
            onClick={() => handleAddToIncident(row)}
            disabled={checkInResource.isPending}
          >
            Add to Incident
          </StylishNewButton>
        );
      },
    },
  ];

  const expandRow: LegacyExpandRowConfig<ResourceSet> = {
    renderer: (row: ResourceSet) => {
      return <ResourceSetCard row={row} selectCallback={editRow} />;
    },
    parentClassName: 'parent-expand-tr',
    className: 'child-expand-tr',
    showExpandColumn: true,
  };

  const handleNewResource = () => {
    if (onNewResource) {
      onNewResource();
    } else {
      setModal('new');
    }
  };

  const kindOptions = [
    { value: 'all', label: 'All Kinds' },
    ...availableKinds.map((kind) => ({ value: kind, label: kind })),
  ];

  return (
    <div className="organization-resources-view">
      {/* Filters and Action Bar */}
      <div className="d-flex mb-3 gap-3">
        <div style={{ width: '250px' }}>
          <StylishNewSelect
            options={kindOptions}
            value={kindOptions.find((opt) => opt.value === selectedKind)}
            onChange={(option: { value: string; label: string } | null) => {
              setSelectedKind(option?.value || 'all');
            }}
            placeholder="Filter by Kind"
          />
        </div>
        <div className="flex-fill">
          <Form.Control
            type="text"
            placeholder="Search resources..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div>
          <StylishNewButton
            className="button--primary"
            onClick={handleNewResource}
          >
            New Resource Set
          </StylishNewButton>
        </div>
        {isOrgAdmin && (
          <div>
            <StylishNewButton
              className="button--secondary"
              onClick={() => {
                forceFetchExternalData.mutateAsync().then(() => {
                  resourceSets.refetch();
                  resourcesInIncident.refetch();
                  setRefreshKey((prev) => prev + 1);
                });
              }}
            >
              Refresh External Data
            </StylishNewButton>
          </div>
        )}
      </div>

      {/* Resources Table */}
      {resourceSets.isSuccess && (
        <LegacyPratusTable
          key={`table-${resourcesInIncident.dataUpdatedAt}-${refreshKey}`}
          keyField={'id'}
          columns={columns}
          rows={filteredAndSortedResources}
          expandRow={expandRow}
        />
      )}

      {/* Modals */}
      {modal === 'new' ? (
        <ResourceSetAddDrawer toggle={() => setModal(undefined)} />
      ) : modal === 'edit' && selectedResource ? (
        <ResourceSetEditDrawer
          toggle={() => setModal(undefined)}
          selectedEntity={selectedResource}
        />
      ) : null}
    </div>
  );
};

export default OrganizationResourcesView;
